import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Loader2, Share2, Plus, X } from 'lucide-react';
// 2.0.0: WelcomeScreen 替代旧的空状态
import { WelcomeScreen } from '../../welcome/components/WelcomeScreen';
import { useChatStore } from '../store/chatStore';
import { getStreamingRecommendations, type RecommendationData } from '../../../shared/services/aiService';
import type { Restaurant } from '../../search/types/restaurant';
import { RestaurantCard } from '../../../shared/components/RestaurantCard';
import type { ChatMessage } from '../types/chat';
import { saveConversation } from '../../../shared/services/conversationService';
import { logger } from '../../../shared/utils/logger';
import {
  createRequestId,
  recordChunkRollback,
  recordIncompleteChunk,
  recordRenderError,
} from '../utils/streamMetrics';
import {
  applyStreamEvent,
  applyChunkWithRollback,
  createStreamMessageState,
  formatFinalText,
  type StreamMessageState,
} from '../utils/streamEventReducer';
// 1.6.0: Agent 模式下，餐厅数据通过 tool_result SSE 直接送达，
// 不再需要前端从文本中提取名称后手动调用 Google Places API 搜索
// import { searchRestaurants, getRestaurantDetails } from '../../../shared/services/googlePlacesService';
import { AuthModal } from '../../account/components/AuthModal';
import { ShareModal } from '../../share/components/ShareModal';
import { createShare } from '../../../shared/services/shareService';
import { FeedbackButtons } from '../../../shared/components/FeedbackButtons';
import { MarkdownRenderer } from '../../../shared/components/MarkdownRenderer';
import { TextBuffer } from '../utils/textBuffer';
import type { StructuredMessage } from '../types/structuredMessage';
// 1.3.8 注释：不再需要从文本解析结构化数据，使用 buildInterleavedSegments 替代
// import { parseStructuredMessage } from '../utils/structuredParser';
// 1.2.21: 导入完整性检测工具
import { ChunkValidator } from '../utils/chunkValidator';
import type { ChunkTag } from '../types/chat';
import { addFavorite, removeFavorite, isFavorite, addPendingFavorite, getFavorites } from '../../../shared/services/favoriteService';
import { getCurrentUser, onAuthStateChange } from '../../../shared/services/authService';
import { getCurrentUserProfile } from '../../../shared/services/profileService'; // 1.2.19: 导入用户资料服务以获取头像
// 1.3.0: 导入GPS定位服务
import { getCurrentPosition, requestLocationPermission, isGeolocationSupported, handleLocationError, checkLocationPermission, type GeolocationPosition } from '../../../shared/services/geolocationService';

// Message segment type for inline rendering
type MessageSegment =
  | { type: 'text'; content: string }
  | { type: 'restaurant'; restaurant: Restaurant; index: number };

interface ChatInterfaceProps {
  language?: string; // 1.2.21: 系统语言（UI显示语言），不影响AI回复语言
  isSidebarCollapsed?: boolean;
  onScroll?: (scrollTop: number, scrollInfo?: { isNearBottom: boolean; isAtBottom: boolean; scrollHeight: number; clientHeight: number; distanceFromBottom: number }) => void;
  onConversationSaved?: () => void; // Callback when conversation is saved to trigger sidebar refresh
}

// 1.5.18: language 参数同时用于 UI 显示和作为后端语言检测的 fallback
// - 后端通过程序化 Unicode 字符集检测确定用户输入语言
// - language 仅在无法从输入推断语言时作为 fallback 使用
export function ChatInterface({ language = 'zh', isSidebarCollapsed = false, onScroll, onConversationSaved }: ChatInterfaceProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  // 1.6.0: Agent 节点进度提示文字（如"🔍 正在理解您的需求..."）
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharingMessageId, setSharingMessageId] = useState<string | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const streamStateRef = useRef<StreamMessageState | null>(null);
  const limitModalShownRef = useRef(false); // Track if limit modal has been shown for current conversation
  const enableScrollDebug = import.meta.env.DEV && import.meta.env.VITE_DEBUG_SCROLL === 'true';
  const [isNearBottom, setIsNearBottom] = useState(true); // Track if user is near bottom (within 150px)
  // 1.2.15: 收藏功能状态
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});
  // 1.2.19: 用户头像URL状态
  const [userAvatarUrl, setUserAvatarUrl] = useState<string | null>(null);
  // 1.3.0: GPS定位相关状态
  const [userLocation, setUserLocation] = useState<GeolocationPosition | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationPermissionRequested, setLocationPermissionRequested] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [pendingSearchQuery, setPendingSearchQuery] = useState<{ message: string; image: File | null; imagePreview: string | null } | null>(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  // 1.2.16: 加载收藏状态 - 优化性能，使用批量查询和缓存
  const chatMessages = useChatStore((state) => state.messages);
  const favoriteStatusCacheRef = useRef<{ restaurantIds: Set<string>; status: Record<string, boolean>; timestamp: number } | null>(null);
  const CACHE_TTL = 10000; // 10秒缓存时间

  // 1.2.16: 提取 loadFavoriteStatus 函数，支持强制刷新参数（需要在 handleFavoriteClick 之前定义）
  const loadFavoriteStatus = useCallback(async (forceRefresh = false) => {
    const currentUser = await getCurrentUser();
    if (!currentUser) {
      setFavoriteStatus({});
      favoriteStatusCacheRef.current = null;
      return;
    }

    // 获取所有消息中的餐厅ID
    const restaurantIds = new Set<string>();
    chatMessages.forEach(message => {
      if (message.restaurants && message.restaurants.length > 0) {
        message.restaurants.forEach(restaurant => {
          restaurantIds.add(restaurant.id);
        });
      }
    });

    if (restaurantIds.size === 0) {
      setFavoriteStatus({});
      favoriteStatusCacheRef.current = null;
      return;
    }

    // 1.2.16: 检查缓存 - 如果强制刷新或缓存无效，跳过缓存
    const now = Date.now();
    const cache = favoriteStatusCacheRef.current;
    const restaurantIdsStr = Array.from(restaurantIds).sort().join(',');
    const cachedIdsStr = cache ? Array.from(cache.restaurantIds).sort().join(',') : '';

    // 如果不强制刷新且缓存有效，使用缓存
    if (!forceRefresh && cache && restaurantIdsStr === cachedIdsStr && (now - cache.timestamp) < CACHE_TTL) {
      setFavoriteStatus(cache.status);
      return;
    }

    // 1.2.16: 优化性能 - 先批量获取所有收藏，然后在内存中匹配
    // 将 N 个查询优化为 1 个查询
    const status: Record<string, boolean> = {};
    try {
      const favorites = await getFavorites();
      const favoriteIds = new Set(favorites.map(fav => fav.id));

      // 在内存中检查每个餐厅是否已收藏
      restaurantIds.forEach(restaurantId => {
        status[restaurantId] = favoriteIds.has(restaurantId);
      });

      // 更新缓存
      favoriteStatusCacheRef.current = {
        restaurantIds: new Set(restaurantIds),
        status,
        timestamp: now
      };
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading favorites:', error);
      }
      // 如果获取收藏失败，将所有状态设为 false
      restaurantIds.forEach(restaurantId => {
        status[restaurantId] = false;
      });
    }
    setFavoriteStatus(status);
  }, [chatMessages]);

  // 1.2.16: 处理收藏点击 - 使用 useCallback 优化性能，修复缓存同步问题
  const handleFavoriteClick = useCallback(async (restaurant: Restaurant) => {
    const currentUser = await getCurrentUser();

    if (!currentUser) {
      // 匿名用户：添加到待收藏列表并打开登录弹窗
      addPendingFavorite(restaurant);
      setShowAuthModal(true);
      return;
    }

    // 登录用户：切换收藏状态
    const isCurrentlyFavorite = favoriteStatus[restaurant.id] || false;
    const newFavoriteStatus = !isCurrentlyFavorite;

    // 1.2.16: 乐观更新 - 立即更新本地状态以提供即时反馈
    setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: newFavoriteStatus }));

    // 1.2.16: 同步更新缓存，避免后续重新加载时的状态不一致
    if (favoriteStatusCacheRef.current) {
      favoriteStatusCacheRef.current.status[restaurant.id] = newFavoriteStatus;
      favoriteStatusCacheRef.current.timestamp = Date.now(); // 更新时间戳以保持缓存新鲜
    }

    try {
      if (isCurrentlyFavorite) {
        // 取消收藏
        const result = await removeFavorite(restaurant.id);
        if (result.success) {
          // 1.2.15: 触发侧边栏刷新（通过 onConversationSaved 回调）
          onConversationSaved?.();
        } else {
          // 如果操作失败，恢复原状态
          setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
          if (favoriteStatusCacheRef.current) {
            favoriteStatusCacheRef.current.status[restaurant.id] = isCurrentlyFavorite;
          }
        }
      } else {
        // 添加收藏
        const result = await addFavorite(restaurant);
        if (result.success) {
          // 1.2.15: 触发侧边栏刷新（通过 onConversationSaved 回调）
          onConversationSaved?.();
        } else {
          // 如果操作失败，恢复原状态
          setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
          if (favoriteStatusCacheRef.current) {
            favoriteStatusCacheRef.current.status[restaurant.id] = isCurrentlyFavorite;
          }
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error toggling favorite:', error);
      }
      // 如果出错，恢复原状态
      setFavoriteStatus(prev => ({ ...prev, [restaurant.id]: isCurrentlyFavorite }));
      if (favoriteStatusCacheRef.current) {
        favoriteStatusCacheRef.current.status[restaurant.id] = isCurrentlyFavorite;
      }
    }
  }, [favoriteStatus, onConversationSaved]);

  useEffect(() => {
    loadFavoriteStatus(false);
  }, [chatMessages, loadFavoriteStatus]);

  // 1.2.19: 加载用户头像
  useEffect(() => {
    const loadUserAvatar = async () => {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        setUserAvatarUrl(null);
        return;
      }

      // 使用getCurrentUserProfile获取用户资料（优先返回缓存）
      const { profile } = await getCurrentUserProfile();
      if (profile?.avatar_url) {
        setUserAvatarUrl(profile.avatar_url);
      } else {
        setUserAvatarUrl(null);
      }
    };

    // 初始加载
    loadUserAvatar();

    // 监听用户登录状态变化
    const { unsubscribe } = onAuthStateChange(async (user) => {
      if (user) {
        await loadUserAvatar();
      } else {
        setUserAvatarUrl(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // 1.3.19: 初始化时检查GPS权限并预加载位置（如果权限已授予）
  // 1.3.19: 添加完整的错误处理，避免影响其他初始化流程
  useEffect(() => {
    const initLocation = async () => {
      try {
        const permissionStatus = await checkLocationPermission();
        if (permissionStatus === 'granted') {
          try {
            const position = await getCurrentPosition();
            setUserLocation(position);
            if (import.meta.env.DEV) {
              logger.log('✅ 初始化时自动获取GPS位置:', position);
            }
          } catch (error) {
            // 静默失败，不显示错误（因为用户可能还没有使用GPS功能）
            if (import.meta.env.DEV) {
              logger.warn('⚠️ 初始化时获取GPS位置失败:', error);
            }
          }
        }
      } catch (error) {
        // 1.3.19: 捕获 checkLocationPermission 可能抛出的异常，避免影响其他初始化流程
        // 静默处理，不显示错误（因为GPS功能是可选的）
        if (import.meta.env.DEV) {
          logger.warn('⚠️ 初始化时检查GPS权限失败:', error);
        }
      }
    };
    
    // 1.3.19: 延迟执行，避免阻塞其他初始化流程（如查询历史加载）
    // 使用 setTimeout 确保其他初始化操作先完成
    const timeoutId = setTimeout(() => {
      initLocation();
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []); // 只在组件挂载时执行一次

  // Function to show limit modal (atomic operation to prevent duplicates)
  const showLimitModalOnce = useCallback(() => {
    if (!limitModalShownRef.current && !showLimitModal) {
      logger.log('✅ Showing limit modal (atomic operation)');
      limitModalShownRef.current = true; // Mark as shown immediately
      setShowLimitModal(true);
      return true;
    } else {
      logger.log('⚠️ Limit modal already shown or visible, skipping:', {
        alreadyShown: limitModalShownRef.current,
        modalVisible: showLimitModal
      });
      return false;
    }
  }, [showLimitModal]);

  // Debug: Log modal state changes
  useEffect(() => {
    logger.log('🔍 Limit modal state changed:', {
      showLimitModal,
      showAuthModal,
      timestamp: new Date().toISOString()
    });
  }, [showLimitModal, showAuthModal]);

  // P1 修复：调试函数使用 import.meta.env.DEV 确保 Vite 生产构建时被完全 tree-shake 移除
  // import.meta.env.DEV 在构建时被静态替换为 false，整个代码块会被死代码消除
  useEffect(() => {
    if (import.meta.env.DEV) {
      /** 手动触发限额弹窗（仅开发环境） */
      (window as unknown as Record<string, unknown>).testShowLimitModal = () => {
        logger.log('🧪 Test: Manually showing limit modal');
        setShowLimitModal(true);
      };

      /** 检查用户限额状态（仅开发环境） */
      (window as unknown as Record<string, unknown>).checkLimitStatus = async () => {
        const { checkQueryLimit, getUserQueryCount } = await import('../../../shared/services/queryHistoryService');
        const { getUserId } = await import('../../../shared/lib/supabase');
        const { getCurrentUser } = await import('../../../shared/services/authService');

        const { userId, isAuthenticated } = await getUserId();
        const user = await getCurrentUser();
        const count = await getUserQueryCount();
        const limitCheck = await checkQueryLimit();

        logger.log('🔍 Limit Status Check:', {
          userId,
          isAuthenticated,
          hasUser: !!user,
          userEmail: user?.email,
          queryCount: count,
          limitCheck,
          showLimitModal,
          showAuthModal
        });

        return {
          userId, isAuthenticated,
          hasUser: !!user, userEmail: user?.email,
          queryCount: count, limitCheck,
          showLimitModal, showAuthModal,
        };
      };

      // 清理：组件卸载时移除 window 上的调试函数
      return () => {
        delete (window as unknown as Record<string, unknown>).testShowLimitModal;
        delete (window as unknown as Record<string, unknown>).checkLimitStatus;
      };
    }
  }, [showLimitModal, showAuthModal]);

  const {
    messages,
    isStreaming,
    addMessage,
    updateMessage,
    appendToMessage,
    setStreaming,
    getConversationHistory,
    updateMessageRecommendations,
    updateMessageRestaurants,
    replaceMessageRestaurantReasons, // 1.3.9: 新增方法，用于更新推荐理由
  } = useChatStore();

  // Get updateMessageWithRestaurants from store state (not destructured to avoid hook dependency issues)
  const getUpdateMessageWithRestaurants = () => useChatStore.getState().updateMessageWithRestaurants;


  // Smart scroll: only scroll to bottom if user is near bottom (within 150px)
  // This allows users to read history without interruption while still auto-scrolling during streaming
  const scrollToBottomIfNear = useCallback(() => {
    if (!chatContainerRef.current || !messagesEndRef.current) return;

    const scrollContainer = chatContainerRef.current;
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Only scroll if user is within 150px of bottom
    if (distanceFromBottom < 150) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Smart scroll during streaming updates - only scroll if user is near bottom
  useEffect(() => {
    if (isStreaming && isNearBottom) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        scrollToBottomIfNear();
      });
    }
  }, [messages, isStreaming, isNearBottom, scrollToBottomIfNear]);

  // Scroll listener for header collapse
  useEffect(() => {
    const scrollContainer = chatContainerRef.current;
    if (!scrollContainer || !onScroll) {
      if (enableScrollDebug) {
        logger.log('Chat scroll listener setup skipped:', {
          hasContainer: !!scrollContainer,
          hasOnScroll: !!onScroll
        });
      }
      return;
    }

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Consider "near bottom" as within 150px for smart scrolling
      const isNearBottomValue = distanceFromBottom < 150;
      const isAtBottom = distanceFromBottom < 10; // Very close to or at bottom

      // Update state for smart scrolling
      setIsNearBottom(isNearBottomValue);

      if (enableScrollDebug) {
        logger.log('Chat scroll event triggered:', { scrollTop, scrollHeight, clientHeight, distanceFromBottom, isNearBottom: isNearBottomValue, isAtBottom });
      }

      // Pass scroll info including isNearBottom and isAtBottom to help detect Safari bounce
      onScroll(scrollTop, { isNearBottom: isNearBottomValue, isAtBottom, scrollHeight, clientHeight, distanceFromBottom });
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollContainer.addEventListener('scroll', throttledHandleScroll, { passive: true });

    if (enableScrollDebug) {
      logger.log('Chat scroll listener attached');
    }

    return () => {
      scrollContainer.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [onScroll]);

  // Handle restaurants received from Function Calling
  // Restaurants are automatically added to the message when received from SSE

  /**
   * Check if error is a query limit error
   */
  const isQueryLimitError = (error: string | null): boolean => {
    if (!error) {
      logger.log('⚠️ isQueryLimitError: error is null or undefined');
      return false;
    }

    // Check for limit error messages (more flexible matching)
    const hasLimitKeyword = error.includes('未注册用户最多只能保存') ||
      error.includes('最多只能保存') ||
      error.includes('请注册以保存更多查询') ||
      error.includes('请注册以保存');

    logger.log('🔍 isQueryLimitError check:', {
      error,
      errorLength: error.length,
      hasLimitKeyword,
      includesLimit: error.includes('未注册用户最多只能保存'),
      includesRegister: error.includes('请注册以保存更多查询'),
      includesLimitGeneric: error.includes('最多只能保存'),
      includesRegisterGeneric: error.includes('请注册以保存')
    });

    if (hasLimitKeyword) {
      logger.log('✅ Query limit error detected:', error);
    }

    return hasLimitKeyword;
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    // Reset file input value immediately to allow selecting the same file again
    // This must be done before processing to ensure onChange fires on subsequent selections
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setUploadedImage(file);
      // Don't analyze image here - will be sent to Gemini when user sends message
    }
  };

  const handleClearImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  // 1.3.0: 检测是否为"附近推荐"意图
  const isNearbyRecommendationQuery = (query: string): boolean => {
    if (!query) return false;
    const lowerQuery = query.toLowerCase();
    // 检测中文、日文、英文的"附近"关键词
    const nearbyKeywords = [
      '附近', '旁边', '周边', '周围',
      '近く', '近くの', '近い',
      'nearby', 'close to', 'near me', 'around me'
    ];
    return nearbyKeywords.some(keyword => lowerQuery.includes(keyword));
  };

  // 1.3.0: 请求GPS定位
  const requestLocation = async (): Promise<GeolocationPosition | null> => {
    if (!isGeolocationSupported()) {
      setLocationError('您的浏览器不支持定位功能');
      return null;
    }

    try {
      const position = await getCurrentPosition();
      setUserLocation(position);
      setLocationError(null);
      return position;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : handleLocationError(error as GeolocationPositionError);
      setLocationError(errorMessage);
      return null;
    }
  };

  // 1.3.0: 执行搜索（带GPS位置）
  const executeSearchWithLocation = async (
    userMessage: string,
    currentImage: File | null,
    currentImagePreview: string | null,
    currentUserLocation: GeolocationPosition | null
  ) => {
    // Add user message with image preview if available
    const userMsgId = addMessage({
      role: 'user',
      content: userMessage || (currentImage ? t('imageUploaded', '已上传图片') : ''),
      status: 'completed',
      imageUrl: currentImagePreview || undefined,
    });

    // Add placeholder for AI response
    const aiMsgId = addMessage({
      role: 'assistant',
      content: '',
      status: 'streaming',
    });

    // Scroll to bottom when starting new AI response
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setIsNearBottom(true);
    }, 100);

    setStreaming(true, aiMsgId);
    setIsTyping(true);
    // 1.6.0: 重置进度提示文字
    setProgressMessage('');

    // Create text buffer for streaming updates
    // 1.4.0: 流式观测 requestId
    const streamRequestId = createRequestId();
    const textBuffer = new TextBuffer({ throttleMs: 100 });

    // 1.2.21: 创建完整性检测器
    const chunkValidator = new ChunkValidator();

    // Track accumulated content for this message
    streamStateRef.current = createStreamMessageState();
    let accumulatedContent = streamStateRef.current.rawText;

    // 1.6.0: Agent 模式标志 — 标记是否通过 tool_result SSE 事件收到了餐厅数据
    // 当为 true 时，onComplete 中跳过遗留的文本提取 + 手动搜索逻辑，
    // 因为 Agent 模式已通过 tool_result 直接发送了结构化餐厅数据
    let receivedToolResult = false;

    // Set flush callback: format text and update message
    textBuffer.setFlushCallback(() => {
      try {
        const streamState = streamStateRef.current;
        const rawText = streamState?.rawText ?? accumulatedContent;
        const formattedText = streamState?.text ?? accumulatedContent;
        const trimmed = rawText.trim();
        const isJSON = trimmed.startsWith('{') && trimmed.endsWith('}');
        const nextContent = isJSON ? rawText : formattedText;

        // Update message with formatted text
        updateMessage(aiMsgId, {
          content: nextContent,
        });

        // Smart scroll: only scroll if user is near bottom
        scrollToBottomIfNear();
      } catch (error) {
        // 1.4.0: 记录渲染异常
        recordRenderError();
        logger.error('❌ Stream render error (flush):', {
          requestId: streamRequestId,
          error,
        });
        throw error;
      }
    });

    try {
      const conversationHistory = getConversationHistory();

      // 1.6.0: 获取当前登录用户 ID，传给后端用于 Mem0 记忆召回
      // getCurrentUser 是异步的（从 Supabase 获取 session），
      // 但非常快（本地 JWT 解析），不会增加用户可感知的延迟
      const currentUser = await getCurrentUser();
      const authUserId = currentUser?.id || undefined;

      // 1.5.18: 语言检测已迁移到后端程序化检测（基于 Unicode 字符集），
      // 前端传 UI 语言作为 fallback，后端会根据用户输入内容确定性判断实际语言
      // 1.3.0: 传递GPS位置参数
      await getStreamingRecommendations(
        userMessage || (currentImage ? t('analyzeImage', '分析这张图片') : ''),
        language,
        conversationHistory,
        // onChunk: add chunk to buffer instead of directly updating
        // 1.2.21: 支持chunk标签和完整性检测
        (chunk: string, tag?: ChunkTag) => {
          const currentRequestId = tag?.requestId || streamRequestId;
          try {
            // 1.2.21: 检测chunk完整性
            const validation = chunkValidator.validateChunk(chunk, accumulatedContent);
            let rollbackContent: string | undefined;
            let shouldRollback = false;
            if (!validation.isComplete) {
              recordIncompleteChunk();
              const rollbackResult = textBuffer.rollbackToLastComplete();
              if (rollbackResult.success && rollbackResult.content) {
                recordChunkRollback();
                rollbackContent = rollbackResult.content;
                shouldRollback = true;
                if (import.meta.env.DEV) {
                  logger.log('✅ 回退成功，已恢复到上一个完整状态:', {
                    requestId: currentRequestId,
                    sequence: rollbackResult.snapshot?.sequence,
                    contentLength: rollbackResult.content.length,
                  });
                }
              } else if (import.meta.env.DEV) {
                logger.warn('⚠️ 回退失败，继续处理当前chunk');
              }
            }

            const currentState = streamStateRef.current ?? createStreamMessageState(accumulatedContent);
            const nextState = applyChunkWithRollback(currentState, chunk, {
              shouldRollback,
              rollbackText: rollbackContent,
            });
            streamStateRef.current = nextState;
            accumulatedContent = nextState.rawText;

            if (shouldRollback && rollbackContent !== undefined) {
              updateMessage(aiMsgId, {
                content: nextState.text,
              });
            }

            // 添加chunk到buffer（传递标签和完整性状态）
            textBuffer.append(chunk, tag, validation.isComplete);
          } catch (error) {
            // 1.4.0: 记录渲染异常
            recordRenderError();
            logger.error('❌ Stream render error (chunk):', {
              requestId: currentRequestId,
              error,
            });
            throw error;
          }
        },
        // onComplete: mark as completed and save conversation history
        async (fullText: string) => {
          // Flush any remaining buffered text
          textBuffer.flush();

          const currentState = streamStateRef.current ?? createStreamMessageState(accumulatedContent);
          const doneState = applyStreamEvent(currentState, {
            type: 'done',
            payload: {},
          });
          streamStateRef.current = doneState;
          accumulatedContent = doneState.rawText;

          const cleanedFinalText = (doneState.rawText || fullText)
            .replace(/<recommendation_data>[\s\S]*?<\/recommendation_data>/g, '')
            .replace(/\{[\s\S]*"recommendedTypes"[\s\S]*\}/g, '')
            .trim();

          // Check if content is JSON format - if so, don't normalize (it will break JSON structure)
          const trimmed = cleanedFinalText.trim();
          const isJSON = trimmed.startsWith('{') && trimmed.endsWith('}');

          // Only normalize if not JSON format
          const finalCleanText = isJSON ? cleanedFinalText : formatFinalText(cleanedFinalText);
          streamStateRef.current = {
            ...doneState,
            rawText: cleanedFinalText,
            text: finalCleanText,
          };
          accumulatedContent = cleanedFinalText;

          // Parse structured data from text buffer
          // 将最终文本设置到buffer中以便解析
          // 1.2.21: append方法签名已更新，tag和isComplete为可选参数
          textBuffer.append(finalCleanText);
          const structuredData = textBuffer.parseStructured();

          // Clean up buffer
          textBuffer.destroy();

          setStreaming(false, null);
          setIsTyping(false);
          // 1.6.0: 清除进度提示
          setProgressMessage('');

          // 1.6.0: Agent 模式下，餐厅数据已通过 tool_result SSE 事件直接送达（onRestaurants 回调），
          // 无需从文本中"猜测"餐厅名称再手动搜索。
          // receivedToolResult=true 表示已收到 tool_result（有餐厅），
          // receivedToolResult=false 但处于 Agent 模式时表示后端判定无需餐厅（如知识类问题），
          // 两种情况都应跳过遗留逻辑，直接用流式文本作为最终内容。
          //
          // 遗留逻辑仅在非 Agent 模式（USE_AGENT_MODE=false 的 legacy proxy）时保留。
          if (receivedToolResult) {
            // Agent 模式且已收到 tool_result：餐厅数据已通过 onRestaurants 附加到消息，
            // 只需更新内容文本和状态为 completed
            logger.log('✅ [Agent] tool_result 已提供餐厅数据，跳过遗留文本提取，直接完成');
            updateMessage(aiMsgId, {
              content: finalCleanText,
              status: 'completed',
            });
          } else {
            // 两种情况会走到这里：
            // 1. Agent 模式但无 tool_result（知识问答、闲聊等非餐厅搜索意图）
            // 2. 非 Agent 模式（legacy proxy，需要从文本中提取餐厅）
            //
            // 判断依据：检查消息是否已有通过 onRestaurants 设置的餐厅数据
            const currentMessage = useChatStore.getState().messages.find(m => m.id === aiMsgId);
            const hasExistingRestaurants = currentMessage?.restaurants && currentMessage.restaurants.length > 0;

            if (hasExistingRestaurants) {
              // 已有餐厅数据（可能来自其他渠道），直接完成
              logger.log('✅ 消息已有餐厅数据，跳过文本提取');
              updateMessage(aiMsgId, {
                content: finalCleanText,
                status: 'completed',
              });
            } else {
              // 无餐厅数据：纯文本回复（知识问答、闲聊等）
              // 直接用流式文本作为最终内容，不进行任何餐厅名称提取或搜索
              logger.log('✅ 纯文本回复，无需餐厅数据，直接完成');
              updateMessage(aiMsgId, {
                content: finalCleanText,
                status: 'completed',
              });
            }
          }

          // Save conversation history to query history
          // This is the ONLY place where we save conversation (after stream completes)
          // All restaurant data from Function Calling (onRestaurants) and manual search is already included
          // If conversationId exists, update existing conversation; otherwise create new one
          // Use setTimeout to ensure all async operations (restaurant search, Function Calling) are complete
          setTimeout(async () => {
            try {
              const { getAllMessages, getCurrentConversationId, setCurrentConversationId } = useChatStore.getState();
              const allMessages = getAllMessages();
              if (allMessages.length > 0) {
                // Filter out messages in error state
                const validMessages = allMessages.filter(msg => msg.status !== 'error');
                if (validMessages.length > 0) {
                  // Check if the AI message has restaurants (from Function Calling or manual search)
                  const aiMessage = validMessages.find(msg => msg.id === aiMsgId);
                  const restaurantCount = aiMessage?.restaurants?.length || 0;

                  const currentConversationId = getCurrentConversationId();
                  logger.log('💾 Saving conversation history in onComplete:', {
                    conversationId: currentConversationId || 'new',
                    messageCount: validMessages.length,
                    restaurantCount: restaurantCount
                  });

                  const result = await saveConversation(
                    validMessages,
                    {
                      language,
                      source: 'chat',
                    },
                    currentConversationId || undefined
                  );

                  logger.log('💾 Save conversation result:', {
                    hasId: !!result.id,
                    hasError: !!result.error,
                    error: result.error,
                    currentConversationId,
                    isNewConversation: !currentConversationId
                  });

                  // If this is a new conversation, save the conversation ID
                  if (result.id && !currentConversationId) {
                    setCurrentConversationId(result.id);
                    logger.log('✅ New conversation created and saved:', {
                      conversationId: result.id,
                    });
                    // Trigger sidebar refresh
                    onConversationSaved?.();
                  } else if (result.id && currentConversationId) {
                    logger.log('✅ Conversation updated successfully:', {
                      conversationId: currentConversationId,
                    });
                    // Trigger sidebar refresh
                    onConversationSaved?.();
                  } else if (result.error) {
                    logger.error('❌ Failed to save conversation history:', result.error);
                    const isLimitError = isQueryLimitError(result.error);
                    const isNewConversation = !currentConversationId;

                    logger.log('🔍 Error analysis:', {
                      error: result.error,
                      errorLength: result.error?.length,
                      errorIncludesLimit: result.error?.includes('未注册用户最多只能保存'),
                      errorIncludesRegister: result.error?.includes('请注册以保存更多查询'),
                      isLimitError,
                      currentConversationId,
                      isNewConversation,
                      shouldShowModal: isNewConversation && isLimitError
                    });

                    // Check if this is a query limit error (only for new conversations)
                    // Use atomic operation to prevent duplicate modal display
                    if (isNewConversation && isLimitError) {
                      showLimitModalOnce();
                    } else {
                      logger.log('⚠️ Not showing limit modal - conditions not met:', {
                        hasConversationId: !!currentConversationId,
                        isLimitError,
                        isNewConversation,
                        alreadyShown: limitModalShownRef.current,
                        modalVisible: showLimitModal
                      });
                    }
                  } else {
                    logger.warn('⚠️ Unexpected result: no item and no error');
                  }
                }
              }
            } catch (error) {
              logger.error('❌ Exception in save conversation history:', error);
              // Check if error contains limit error message
              const errorMessage = error instanceof Error ? error.message : String(error);
              if (errorMessage && (errorMessage.includes('未注册用户最多只能保存') || errorMessage.includes('请注册以保存更多查询'))) {
                const currentConversationId = useChatStore.getState().getCurrentConversationId();
                // Use atomic operation to prevent duplicate modal display
                if (!currentConversationId) {
                  showLimitModalOnce();
                } else {
                  logger.log('⚠️ Not showing limit modal from exception handler - has conversation ID');
                }
              }
            }
          }, 500); // Delay to ensure all restaurant data from Function Calling and manual search is included
        },
        // onRecommendation: handle recommendation data (optional, for backward compatibility)
        (recommendation: RecommendationData) => {
          logger.log('📋 Received recommendation data:', recommendation);
          // Note: We still support recommendation data, but restaurants come directly from Function Calling
          updateMessageRecommendations(aiMsgId, recommendation);
        },
        // onRestaurants: handle restaurant data from Function Calling
        // 1.3.8: 修改回调，同时接收 restaurantReasons（与 restaurants 按索引对齐）
        (restaurants: Restaurant[], restaurantReasons?: string[]) => {
          // 1.6.0: 标记已收到 tool_result，onComplete 将跳过遗留文本提取逻辑
          receivedToolResult = true;

          if (!restaurants || !Array.isArray(restaurants)) {
            logger.warn('⚠️ Received invalid restaurant data (not an array):', restaurants);
            return;
          }

          if (restaurants.length === 0) {
            logger.warn('⚠️ Received empty restaurant array from Function Calling');
            return;
          }

          // Validate restaurant data format
          const validRestaurants = restaurants.filter(r =>
            r &&
            typeof r === 'object' &&
            r.id &&
            typeof r.id === 'string' &&
            r.name &&
            typeof r.name === 'string'
          );

          if (validRestaurants.length === 0) {
            logger.warn('⚠️ No valid restaurants found after validation:', restaurants);
            return;
          }

          if (validRestaurants.length < restaurants.length) {
            logger.warn(`⚠️ Filtered out ${restaurants.length - validRestaurants.length} invalid restaurants`);
          }

          // 1.3.8: 记录 reasons 信息
          const validReasons = restaurantReasons || [];
          const reasonsWithContent = validReasons.filter(r => r && r.trim()).length;

          logger.log(`✅ Received ${validRestaurants.length} restaurants with ${reasonsWithContent}/${validReasons.length} reasons from Function Calling:`, validRestaurants.map((r, idx) => ({
            id: r.id,
            name: r.name,
            nameZh: r.nameZh,
            hasReason: !!(validReasons[idx] && validReasons[idx].trim()),
          })));

          // Add restaurants and reasons to the message immediately
          // 1.3.8: 同时传递 restaurantReasons
          updateMessageRestaurants(aiMsgId, validRestaurants, validReasons.length > 0 ? validReasons : undefined);

          // Log after update to verify data is stored
          setTimeout(() => {
            const updatedMessage = useChatStore.getState().messages.find(m => m.id === aiMsgId);
            if (updatedMessage) {
              logger.log(`✅ 餐厅数据已更新到消息 ${aiMsgId}:`, {
                messageRestaurantsCount: updatedMessage.restaurants?.length || 0,
                messageReasonsCount: updatedMessage.restaurantReasons?.length || 0,
                reasonsWithContent: updatedMessage.restaurantReasons?.filter(r => r && r.trim()).length || 0,
              });
            }
          }, 100);
        },
        // 1.3.9: onRestaurantsUpdate: 处理推荐理由更新（后端发送updated:true时调用）
        (restaurantReasons: string[]) => {
          if (!aiMsgId) return;

          // 替换整个推荐理由数组
          replaceMessageRestaurantReasons(aiMsgId, restaurantReasons);

          const reasonsWithContent = restaurantReasons.filter(r => r && r.trim()).length;
          logger.log(`🔄 Updated restaurant reasons for message ${aiMsgId}:`, {
            totalReasons: restaurantReasons.length,
            reasonsWithContent,
          });
        },
        // onError: handle errors
        (error: Error) => {
          // Clean up buffer on error
          textBuffer.destroy();
          // 1.2.21: 清理完整性检测器
          chunkValidator.reset();

          updateMessage(aiMsgId, {
            status: 'error',
            error: error.message,
          });
          setStreaming(false, null);
          setIsTyping(false);
          // 1.6.0: 清除进度提示
          setProgressMessage('');
        },
        // imageFile: pass image file directly to Gemini
        currentImage || undefined,
        // 1.3.0: 传递GPS位置参数
        currentUserLocation || undefined,
        // 1.4.0: 传递观测requestId
        streamRequestId,
        // 1.6.0: onProgress 回调 — 展示 Agent 各节点的实时处理进度
        (_step: string, message: string) => {
          setProgressMessage(message);
        },
        // 1.6.0: 传递登录用户 ID，用于后端 Mem0 记忆召回和个性化推荐
        authUserId,
      );
    } catch (error) {
      // Clean up buffer on error
      textBuffer.destroy();
      // 1.2.21: 清理完整性检测器
      chunkValidator.reset();

      logger.error('Chat error:', error);
      updateMessage(aiMsgId, {
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      setStreaming(false, null);
      setIsTyping(false);
      // 1.6.0: 清除进度提示
      setProgressMessage('');
    }
  };

  // 1.3.0: handleSend函数 - 检查GPS定位需求并执行搜索
  // 1.3.19: 优化GPS权限检查逻辑，避免重复弹窗
  const handleSend = async () => {
    if ((!input.trim() && !uploadedImage) || isStreaming) return;

    // Reset limit modal shown flag for new conversation
    limitModalShownRef.current = false;

    const userMessage = input.trim();
    const currentImage = uploadedImage;
    const currentImagePreview = imagePreview;

    // 1.3.0: 检测"附近推荐"意图，检查GPS定位需求
    // 1.3.19: 先检查浏览器是否已经有定位权限，避免重复弹窗
    // 1.3.45: 无论是否有GPS位置，检测到"附近"关键字时都刷新GPS定位，确保使用最新位置
    if (isNearbyRecommendationQuery(userMessage)) {
      // 先检查浏览器是否已经有定位权限
      try {
        const permissionStatus = await checkLocationPermission();
        
        if (permissionStatus === 'granted') {
          // 1.3.19: 权限已授予，直接获取位置，不弹窗
          // 1.3.45: 无论是否已有GPS位置，都刷新GPS定位，确保使用最新位置
          try {
            logger.log('📍 检测到"附近"关键字，刷新GPS位置...');
            const position = await getCurrentPosition();
            setUserLocation(position);
            // 继续执行搜索
            setInput('');
            setUploadedImage(null);
            setImagePreview(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            await executeSearchWithLocation(userMessage, currentImage, currentImagePreview, position);
            return;
          } catch (error) {
            logger.error('❌ 获取GPS位置失败:', error);
            // 1.3.45: 如果GPS获取失败，降级使用旧位置（如果有）
            if (userLocation) {
              logger.warn('⚠️ GPS获取失败，使用旧位置继续搜索');
              setInput('');
              setUploadedImage(null);
              setImagePreview(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
              await executeSearchWithLocation(userMessage, currentImage, currentImagePreview, userLocation);
              return;
            }
            // 如果没有旧位置，继续显示弹窗让用户重新授权
          }
        }
      } catch (error) {
        logger.error('❌ 检查GPS权限失败:', error);
        // 1.3.45: 如果权限检查失败，但有旧位置，使用旧位置继续搜索
        if (userLocation) {
          logger.warn('⚠️ 权限检查失败，使用旧位置继续搜索');
          setInput('');
          setUploadedImage(null);
          setImagePreview(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          await executeSearchWithLocation(userMessage, currentImage, currentImagePreview, userLocation);
          return;
        }
        // 如果没有旧位置，继续执行搜索（不使用GPS）
      }
      
      // 1.3.19: 权限未授予或需要提示，显示授权弹窗
      if (!locationPermissionRequested) {
        setLocationPermissionRequested(true);
        setShowLocationModal(true);
        // 保存待搜索的查询，等待用户授权后执行
        setPendingSearchQuery({
          message: userMessage,
          image: currentImage,
          imagePreview: currentImagePreview
        });
        // 清空输入，但不执行搜索
        setInput('');
        setUploadedImage(null);
        setImagePreview(null);
        // 重置文件输入
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return; // 不继续执行搜索，等待用户授权
      }
    }

    // 先清空输入框，然后执行搜索
    setInput('');
    setUploadedImage(null);
    setImagePreview(null);

    // Reset file input to allow uploading again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // 如果不需要GPS或已有位置，直接执行搜索
    await executeSearchWithLocation(userMessage, currentImage, currentImagePreview, userLocation);
  };

  /**
   * 2.0.0: 预设问题点击处理
   * 将预设问题文本填入输入框并自动发送，实现"一键提问"体验。
   * 直接调用 executeSearchWithLocation 而非设置 input 后触发 handleSend，
   * 避免 React 状态异步更新导致的时序问题。
   */
  const handlePresetClick = async (question: string) => {
    if (isStreaming) return;
    setInput('');
    await executeSearchWithLocation(question, null, null, userLocation);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Handle share button click for an AI message
   */
  const handleShareMessage = useCallback(async (message: ChatMessage) => {
    if (!message.content || message.status !== 'completed') {
      logger.warn('Cannot share message: message is not completed or has no content');
      return;
    }

    try {
      setIsCreatingShare(true);
      setSharingMessageId(message.id);

      // Find the user question for this AI reply
      // The user message should be the one before this AI message
      const messageIndex = messages.findIndex(m => m.id === message.id);
      const previousMessages = messages.slice(0, messageIndex);
      const userMessage = previousMessages.reverse().find(m => m.role === 'user');
      const queryText = userMessage?.content || '';

      // Create share link
      const shareResponse = await createShare({
        message_id: message.id,
        message_content: message.content,
        query_text: queryText,
        restaurants: message.restaurants || [],
      });

      if (!shareResponse.success || !shareResponse.share_url) {
        throw new Error(shareResponse.error || 'Failed to create share link');
      }

      const shareUrl = shareResponse.share_url;

      // Try to use Web Share API (if supported)
      if (navigator.share) {
        try {
          await navigator.share({
            title: t('shareTitle', 'Fun with AI 推荐'),
            text: message.content.substring(0, 100) + (message.content.length > 100 ? '...' : ''),
            url: shareUrl,
          });
          logger.log('✅ Shared via Web Share API');
          // Share successful, we're done
          return;
        } catch (shareError: any) {
          // User cancelled or share failed, fall through to custom modal
          if (shareError.name === 'AbortError') {
            logger.log('User cancelled share');
            return;
          }
          logger.warn('Web Share API failed, falling back to custom modal:', shareError);
          // Fall through to show custom modal
        }
      }

      // Fallback: Show custom share modal
      setShareUrl(shareUrl);
      setShowShareModal(true);
    } catch (error: any) {
      logger.error('Error sharing message:', error);
      // Optionally show error message to user
      alert(t('shareError', '分享失败，请稍后重试'));
    } finally {
      setIsCreatingShare(false);
      setSharingMessageId(null);
    }
  }, [messages, t]);

  /**
  // 1.6.0: 删除 processBoldMarkers 和 formatRestaurantText 本地副本
  // - processBoldMarkers: XMarkdown (v1.3.44) 已接管加粗标记渲染，此函数反而会破坏编号列表格式
  // - formatRestaurantText: 1.3.7 已禁用（直接返回原文），1.3.8 标记为 legacy
  // 所有 Markdown 渲染（加粗、列表编号、段落）完全交给 XMarkdown 处理

  /**
   * 1.3.8: 从 content 文本中解析餐厅信息和推荐理由
   * 格式：n. 餐廳名 (類型) - 評分x.x 地址: xxx 推薦理由
   * 示例：1. 愛彩 (日式料理) - 评分4.9 地址: 日本、〒342-0035... 这家日式餐厅拥有非常高的评分...
   */
  const parseRestaurantsFromContent = (content: string, restaurants: Restaurant[]): Array<{
    index: number;
    fullText: string;
    reason: string;
    restaurant: Restaurant | null;
  }> => {
    const results: Array<{
      index: number;
      fullText: string;
      reason: string;
      restaurant: Restaurant | null;
    }> = [];

    if (!content || !content.trim()) {
      return results;
    }

    // 匹配模式：数字. 餐廳名 (類型) - 评分x.x 地址: xxx 推薦理由
    // 注意：评分后面可能没有冒号（"评分4.9"），地址后面直接是推荐理由
    // 支持"评分"和"評分"两种写法
    // 使用更宽松的匹配，匹配到下一个序号或文本结尾
    const pattern = /(\d+)[\.、]\s*([^（\(]+?)\s*[（\(]([^）\)]+?)[）\)]\s*-\s*[評评]分[：:]?\s*(\d+\.?\d*)\s*地址[：:]\s*([^\n]+?)(?=\n\d+[\.、]|$)/g;

    let match;
    while ((match = pattern.exec(content)) !== null) {
      const index = parseInt(match[1], 10);
      const restaurantName = match[2].trim();
      const cuisineType = match[3].trim();
      const rating = match[4];
      const addressAndReason = match[5].trim(); // 地址和推荐理由在一起
      const fullText = match[0].trim();

      // 从地址和推荐理由中分离出推荐理由
      // 地址格式：日本、〒342-0035 埼玉県吉川市高久２丁目７−１
      // 推荐理由在地址之后，通常是中文描述性文字
      let reason = '';

      // 查找地址的结束位置
      // 地址通常以"日本、"开头，包含邮政编码"〒"，以"丁目"、"号"、"階"等结尾
      // 推荐理由通常是中文描述，不包含日文地址关键词

      // 查找最后一个地址关键词的位置（丁目、号、階、街区等）
      const addressEndKeywords = ['丁目', '号', '階', '街区', '棟', 'F', '1F', '2F', '3F'];
      let lastAddressKeywordPos = -1;

      for (const keyword of addressEndKeywords) {
        const lastIndex = addressAndReason.lastIndexOf(keyword);
        if (lastIndex > lastAddressKeywordPos) {
          // 找到关键词后的位置（跳过关键词本身和可能的数字）
          const afterKeyword = addressAndReason.substring(lastIndex + keyword.length);
          // 查找关键词后的数字或空格结束位置
          const numberMatch = afterKeyword.match(/^\s*[\d-]+\s*/);
          if (numberMatch) {
            lastAddressKeywordPos = lastIndex + keyword.length + numberMatch[0].length;
          } else {
            lastAddressKeywordPos = lastIndex + keyword.length;
          }
        }
      }

      if (lastAddressKeywordPos > 0) {
        reason = addressAndReason.substring(lastAddressKeywordPos).trim();
      } else {
        // 如果找不到地址关键词，尝试找到邮政编码后的长地址（至少30个字符）
        const zipCodeMatch = addressAndReason.match(/〒[\d-]+/);
        if (zipCodeMatch && zipCodeMatch.index !== undefined) {
          const afterZipCode = addressAndReason.substring(zipCodeMatch.index + zipCodeMatch[0].length);
          // 假设地址后35个字符之后是推荐理由
          reason = afterZipCode.length > 35 ? afterZipCode.substring(35).trim() : '';
        }
      }

      // 清理推荐理由：移除可能的地址残留（如果推荐理由以地址格式开头）
      if (reason && (reason.startsWith('日本、') || reason.startsWith('〒'))) {
        reason = '';
      }

      // 匹配 restaurants 数组中的真实餐厅对象
      // 优先按索引匹配（如果 restaurants 数组顺序正确）
      let matchedRestaurant: Restaurant | null = null;

      // 方法1：按索引匹配（索引从1开始，数组索引从0开始）
      if (index > 0 && index <= restaurants.length) {
        const candidate = restaurants[index - 1];
        // 验证名称是否匹配（至少部分匹配）
        const nameMatches =
          candidate.name === restaurantName ||
          candidate.nameZh === restaurantName ||
          candidate.nameJa === restaurantName ||
          restaurantName.includes(candidate.name) ||
          candidate.name.includes(restaurantName) ||
          (candidate.nameZh && restaurantName.includes(candidate.nameZh)) ||
          (candidate.nameZh && candidate.nameZh.includes(restaurantName));

        if (nameMatches) {
          matchedRestaurant = candidate;
        }
      }

      // 方法2：如果按索引匹配失败，尝试名称匹配
      if (!matchedRestaurant) {
        matchedRestaurant = restaurants.find(r => {
          const nameMatch = r.name === restaurantName ||
            r.nameZh === restaurantName ||
            r.nameJa === restaurantName;
          // 也可以尝试模糊匹配（包含关系）
          if (!nameMatch) {
            return restaurantName.includes(r.name) ||
              r.name.includes(restaurantName) ||
              (r.nameZh && restaurantName.includes(r.nameZh)) ||
              (r.nameZh && r.nameZh.includes(restaurantName));
          }
          return nameMatch;
        }) || null;
      }

      results.push({
        index,
        fullText,
        reason: reason || '', // 如果没有提取到推荐理由，使用空字符串
        restaurant: matchedRestaurant
      });
    }

    // 按序号排序
    results.sort((a, b) => a.index - b.index);

    return results;
  };

  /**
   * 1.3.8: 构建交错渲染的 segments（主路径）
   * 策略：从 content 中解析餐厅信息，提取推荐理由，交错插入卡片
   * 
   * @param message - 聊天消息对象
   * @returns MessageSegment[] - 渲染用的 segment 数组
   */
  const buildInterleavedSegments = (message: ChatMessage): MessageSegment[] => {
    const segments: MessageSegment[] = [];
    const { content, restaurants = [], restaurantReasons = [], status } = message;

    // 1.3.8: 如果 content 是 JSON 格式（旧格式），提取 introText
    let actualContent = content;
    if (content && content.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(content);
        if (parsed.introText) {
          actualContent = parsed.introText;
          // 如果有 outroText，也合并进来
          if (parsed.outroText) {
            actualContent += '\n\n' + parsed.outroText;
          }
        }
      } catch (e) {
        // JSON 解析失败，使用原始 content
        actualContent = content;
      }
    }

    // 如果 content 包含餐厅列表格式，从 content 中解析
    if (actualContent && actualContent.trim() && restaurants.length > 0) {
      const parsedRestaurants = parseRestaurantsFromContent(actualContent, restaurants);

      if (parsedRestaurants.length > 0) {
        // 提取 intro（第一个餐厅之前的内容）
        const firstMatchIndex = actualContent.search(/\d+[\.、]/);
        if (firstMatchIndex > 0) {
          let introText = actualContent.substring(0, firstMatchIndex).trim();
          // 清理多余的空白行
          introText = introText.replace(/\n{3,}/g, '\n\n');
          // 只保留简短的开场白（如果 intro 太长，可能包含多余的介绍文本）
          // 如果 intro 超过 200 字符，只保留前 200 字符
          if (introText.length > 200) {
            introText = introText.substring(0, 200).trim() + '...';
          }
          if (introText) {
            segments.push({ type: 'text', content: introText });
          }
        }

        // 对每个解析出的餐厅，添加文本和卡片（交错显示）
        for (const parsed of parsedRestaurants) {
          if (parsed.restaurant) {
            // 1.3.9: 如果有 restaurantReasons,使用它替换 parsed.fullText 中的理由部分
            let textContent = parsed.fullText.trim();
            
            // 1.3.11: 改进推荐理由插入逻辑，添加调试日志
            if (restaurantReasons && restaurantReasons.length > 0) {
              const reasonIndex = parsed.index - 1; // index从1开始,数组从0开始
              if (reasonIndex >= 0 && reasonIndex < restaurantReasons.length && restaurantReasons[reasonIndex] && restaurantReasons[reasonIndex].trim()) {
                // 提取标题部分(包含序号和餐厅名)
                const titleMatch = textContent.match(/^(\d+[\.、]\s*\*\*[^*]+\*\*)/);
                if (titleMatch) {
                  // 用AI生成的理由替换原文本
                  textContent = `${titleMatch[1]}\n\n${restaurantReasons[reasonIndex]}`;
                  if (import.meta.env.DEV) {
                    console.log(`✅ [1.3.11] Inserted reason for restaurant ${parsed.index}:`, {
                      restaurantName: parsed.restaurant.name,
                      reasonLength: restaurantReasons[reasonIndex].length,
                      reasonPreview: restaurantReasons[reasonIndex].substring(0, 50)
                    });
                  }
                } else {
                  // 如果没有匹配到标题，直接使用标题和推荐理由
                  textContent = `**${parsed.index}. ${parsed.restaurant.name}**\n\n${restaurantReasons[reasonIndex]}`;
                  if (import.meta.env.DEV) {
                    console.log(`✅ [1.3.11] Inserted reason (no title match) for restaurant ${parsed.index}:`, {
                      restaurantName: parsed.restaurant.name,
                      reasonLength: restaurantReasons[reasonIndex].length
                    });
                  }
                }
              } else {
                // 如果没有推荐理由，只保留标题部分
                if (import.meta.env.DEV) {
                  console.warn(`⚠️ [1.3.11] No reason for restaurant ${parsed.index}:`, {
                    restaurantName: parsed.restaurant.name,
                    reasonIndex,
                    reasonsLength: restaurantReasons.length,
                    hasReason: reasonIndex >= 0 && reasonIndex < restaurantReasons.length ? !!restaurantReasons[reasonIndex] : false
                  });
                }
                const titleMatch = textContent.match(/^(\d+[\.、]\s*\*\*[^*]+\*\*)/);
                if (titleMatch) {
                  textContent = titleMatch[1];
                } else {
                  textContent = `**${parsed.index}. ${parsed.restaurant.name}**`;
                }
              }
            } else {
              // 如果没有 restaurantReasons，只保留标题部分
              if (import.meta.env.DEV) {
                console.warn(`⚠️ [1.3.11] No restaurantReasons array for restaurant ${parsed.index}:`, {
                  restaurantName: parsed.restaurant.name,
                  hasReasons: !!restaurantReasons,
                  reasonsLength: restaurantReasons?.length || 0
                });
              }
              const titleMatch = textContent.match(/^(\d+[\.、]\s*\*\*[^*]+\*\*)/);
              if (titleMatch) {
                textContent = titleMatch[1];
              } else {
                textContent = `**${parsed.index}. ${parsed.restaurant.name}**`;
              }
            }

            // 添加餐厅文本（包含推荐理由）
            segments.push({
              type: 'text',
              content: textContent
            });

            // 添加餐厅卡片（插入到文本之后）
            segments.push({
              type: 'restaurant',
              restaurant: parsed.restaurant,
              index: parsed.index
            });
          } else {
            // 如果没有匹配到餐厅对象，只添加文本
            segments.push({
              type: 'text',
              content: parsed.fullText
            });
          }
        }

        return segments;
      }
    }

    // Fallback: 如果无法从 content 解析，使用原有的逻辑
    // 1. 添加 intro（如果有）
    if (actualContent && actualContent.trim()) {
      let introText = actualContent.trim();
      // 清理多余的空白行
      introText = introText.replace(/\n{3,}/g, '\n\n');
      // 只保留简短的开场白（如果 intro 太长，可能包含多余的介绍文本）
      // 如果 intro 超过 200 字符，只保留前 200 字符
      if (introText.length > 200) {
        introText = introText.substring(0, 200).trim() + '...';
      }
      if (introText) {
        segments.push({ type: 'text', content: introText });
      }
    }

    // 2. 计算配对数量（streaming 下 reasons 可能更短）
    const pairCount = Math.min(restaurants.length, restaurantReasons.length);
    
    // 1.3.11: 添加调试日志
    if (import.meta.env.DEV) {
      console.log('🔍 [1.3.11] buildInterleavedSegments Fallback:', {
        restaurantsCount: restaurants.length,
        reasonsCount: restaurantReasons.length,
        pairCount,
        hasReasons: restaurantReasons.length > 0,
        reasonsWithContent: restaurantReasons.filter(r => r && r.trim()).length
      });
    }

    // 3. 交错渲染：理由 + 卡片
    for (let i = 0; i < pairCount; i++) {
      const restaurant = restaurants[i];
      const reason = restaurantReasons[i] || '';

      // 前端生成序号和标题（不依赖 LLM 输出格式）
      const title = `**${i + 1}. ${restaurant.name}**`;
      const reasonText = reason.trim();

      // 组合标题和理由
      const combinedText = reasonText
        ? `${title}\n\n${reasonText}`
        : title;

      if (import.meta.env.DEV && reasonText) {
        console.log(`✅ [1.3.11] Fallback: Inserted reason for restaurant ${i + 1}:`, {
          restaurantName: restaurant.name,
          reasonLength: reasonText.length,
          reasonPreview: reasonText.substring(0, 50)
        });
      }

      segments.push({ type: 'text', content: combinedText });
      segments.push({
        type: 'restaurant',
        restaurant: restaurant,
        index: i + 1
      });
    }

    // 4. 剩余餐厅兜底（reasons 缺失或 streaming 未生成完）
    for (let i = pairCount; i < restaurants.length; i++) {
      const restaurant = restaurants[i];

      // done 且完全没 reasons 时补标题，保持一致性
      if (status === 'completed' && restaurantReasons.length === 0) {
        segments.push({
          type: 'text',
          content: `**${i + 1}. ${restaurant.name}**`
        });
      }

      segments.push({
        type: 'restaurant',
        restaurant: restaurant,
        index: i + 1
      });
    }

    return segments;
  };

  /**
   * 1.3.8: 检查是否应该使用结构化对齐渲染（方案 C）
   * 条件：有 restaurants 数据（restaurantReasons 可选）
   */
  const shouldUseStructuredRendering = (message: ChatMessage): boolean => {
    return !!(message.restaurants && message.restaurants.length > 0);
  };

  // ========================================
  // 1.3.8: 以下函数已废弃，保留作为历史参考
  // 新的渲染策略使用 buildInterleavedSegments 基于结构化数据对齐渲染
  // 不再需要从文本中解析餐厅名称
  // ========================================

  /* 1.3.8 注释：旧的名称匹配函数，已被结构化对齐策略替代
  /**
   * Match restaurant name in text (exact match only)
   * Supports Chinese, English, and Japanese names
   *\/
  const matchRestaurantName = (text: string, restaurant: Restaurant): { found: boolean; position?: number } => {
    const name = restaurant.name;
    const nameZh = restaurant.nameZh;
    const nameJa = restaurant.nameJa;
    
    // Try exact match with Chinese name first (if available)
    if (nameZh) {
      const nameZhIndex = text.indexOf(nameZh);
      if (nameZhIndex !== -1) {
        return { found: true, position: nameZhIndex };
      }
    }
    
    // Try exact match with Japanese name (if available)
    if (nameJa) {
      const nameJaIndex = text.indexOf(nameJa);
      if (nameJaIndex !== -1) {
        return { found: true, position: nameJaIndex };
      }
    }
    
    // Try exact match with main name (English)
    const nameIndex = text.indexOf(name);
    if (nameIndex !== -1) {
      return { found: true, position: nameIndex };
    }
    
    // No match found - exact match only
    return { found: false };
  };
  */ // end 1.3.8 注释：matchRestaurantName

  /* 1.3.8 注释：旧的文本解析函数，已被结构化对齐策略替代
  /**
   * Parse message text and identify restaurant introduction positions
   * Returns segments array for inline rendering
   * 
   * For streaming messages: Show restaurants at the end to avoid position mismatches
   * For completed messages: Match restaurants to their positions in text
   *\/
  const parseMessageWithRestaurants = (text: string, restaurants: Restaurant[], messageStatus?: string): MessageSegment[] => {
    if (!restaurants || restaurants.length === 0) {
      return [{ type: 'text', content: text }];
    }

    // For streaming messages, delay restaurant card matching
    // Show all restaurants at the end to avoid position mismatches while text is still updating
    if (messageStatus === 'streaming') {
      const result: MessageSegment[] = [{ type: 'text', content: text }];
      restaurants.forEach((restaurant, index) => {
        result.push({
          type: 'restaurant',
          restaurant: restaurant,
          index: index
        });
      });
      return result;
    }

    // For completed messages, perform precise matching
    const segments: MessageSegment[] = [];
    let lastIndex = 0;
    const usedRestaurants = new Set<number>();
    
    // Sort restaurants by their position in text (if found)
    const restaurantsWithPositions = restaurants
      .map((restaurant, index) => {
        const match = matchRestaurantName(text, restaurant);
        return {
          restaurant,
          index,
          position: match.found ? match.position : -1
        };
      })
      .filter(item => item.position !== -1)
      .sort((a, b) => a.position! - b.position!);

    // Process restaurants in order of appearance
    for (const item of restaurantsWithPositions) {
      if (usedRestaurants.has(item.index)) continue;

      const { restaurant, index, position } = item;
      
      // Find the end of this restaurant's introduction
      // Look for patterns that indicate end of current restaurant intro:
      // 1. Next numbered item (e.g., "2. 餐厅名")
      // 2. Next restaurant name
      // 3. Double newline (paragraph break)
      // 4. End of text
      
      let endPosition = position!;
      
      // Find next numbered item after current position
      const afterCurrent = text.substring(position! + 1);
      const nextNumberedMatch = afterCurrent.match(/^\n\s*(\d+)[\.、]\s/m);
      const nextNumberedPos = nextNumberedMatch 
        ? position! + 1 + afterCurrent.indexOf(nextNumberedMatch[0])
        : Infinity;
      
      // Find next restaurant name
      const nextRestaurantPos = restaurantsWithPositions
        .filter(r => r.position! > position!)
        .map(r => r.position!)
        .sort((a, b) => a - b)[0] || Infinity;
      
      // Find double newline
      const doubleNewlinePos = text.indexOf('\n\n', position!);
      
      // Determine end position (take the earliest valid position)
      const candidates = [
        nextNumberedPos !== Infinity ? nextNumberedPos : Infinity,
        nextRestaurantPos !== Infinity ? nextRestaurantPos : Infinity,
        doubleNewlinePos !== -1 ? doubleNewlinePos : Infinity,
      ].filter(p => p !== Infinity && p > position!);
      
      endPosition = candidates.length > 0 
        ? Math.min(...candidates)
        : text.length;

      // Add text before restaurant
      if (position! > lastIndex) {
        const beforeText = text.substring(lastIndex, position!).trim();
        if (beforeText) {
          segments.push({
            type: 'text',
            content: beforeText
          });
        }
      }

      // Add restaurant introduction text
      const restaurantIntro = text.substring(position!, endPosition).trim();
      if (restaurantIntro) {
        segments.push({
          type: 'text',
          content: restaurantIntro
        });
      }

      // Add restaurant card immediately after introduction text
      segments.push({
        type: 'restaurant',
        restaurant: restaurant,
        index: index
      });

      usedRestaurants.add(index);
      lastIndex = endPosition;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex).trim();
      if (remainingText) {
        segments.push({
          type: 'text',
          content: remainingText
        });
      }
    }

    // If no restaurants were matched in text, show all restaurants at the end
    if (segments.length === 0 || (segments.length === 1 && segments[0].type === 'text')) {
      // Return text first, then all restaurant cards
      const result: MessageSegment[] = [{ type: 'text', content: text }];
      restaurants.forEach((restaurant, index) => {
        result.push({
          type: 'restaurant',
          restaurant: restaurant,
          index: index
        });
      });
      return result;
    }

    // If there are unmatched restaurants, add them at the end
    const unmatchedRestaurants = restaurants.filter((_, index) => !usedRestaurants.has(index));
    if (unmatchedRestaurants.length > 0) {
      unmatchedRestaurants.forEach((restaurant) => {
        segments.push({
          type: 'restaurant',
          restaurant: restaurant,
          index: restaurants.indexOf(restaurant)
        });
      });
    }

    return segments;
  };
  */ // end 1.3.8 注释：parseMessageWithRestaurants

  /**
   * 1.3.8: Render message content with inline restaurant cards
   * 策略优先级：C（结构化交错）> A（纯卡片末尾）> 旧文本解析 fallback
   * 
   * 核心改进：
   * 1. 使用 buildInterleavedSegments 基于结构化数据对齐渲染（主路径）
   * 2. 前端自动生成序号，不依赖 LLM 文本格式
   * 3. 修正 streaming 光标定位到最后一个 text segment
   * 4. 优化 key 策略：card-${message.id}-${restaurant.id}
   */
  const renderMessageContent = (message: ChatMessage) => {
    const originalContent = message.content || '';
    const { restaurants = [], restaurantReasons = [] } = message;

    // ========================================
    // 策略 C：结构化对齐 + 数据渲染（主路径）
    // 条件：有 restaurants 数据
    // ========================================
    if (shouldUseStructuredRendering(message)) {
      // 检查是否有 restaurantReasons（完整交错模式）
      const hasReasons = restaurantReasons && restaurantReasons.length > 0;
      
      // 1.3.11: 添加调试日志
      if (import.meta.env.DEV) {
        console.log('🔍 [1.3.11] renderMessageContent:', {
          messageId: message.id,
          restaurantsCount: restaurants.length,
          reasonsCount: restaurantReasons.length,
          hasReasons,
          reasonsWithContent: restaurantReasons.filter(r => r && r.trim()).length,
          reasonsPreview: restaurantReasons.slice(0, 3).map(r => r ? r.substring(0, 30) : 'empty')
        });
      }

      if (hasReasons) {
        // 方案 C 完整模式：理由-卡片交错渲染
        const segments = buildInterleavedSegments(message);

        // 找到最后一个 text segment 的下标（用于 streaming 光标）
        let lastTextIndex = -1;
        for (let i = segments.length - 1; i >= 0; i--) {
          if (segments[i].type === 'text') {
            lastTextIndex = i;
            break;
          }
        }

        logger.log('🎯 [1.3.8] 使用策略 C 完整模式（结构化交错）:', {
          messageId: message.id,
          restaurantsCount: restaurants.length,
          reasonsCount: restaurantReasons.length,
          segmentsCount: segments.length,
          lastTextIndex,
        });

        return (
          <div className="space-y-3">
            {segments.map((segment, idx) => {
              if (segment.type === 'text') {
                // Key 策略：text-${message.id}-${idx}
                const isStreaming = message.status === 'streaming' && idx === lastTextIndex;
                return (
                  <MarkdownRenderer
                    key={`text-${message.id}-${idx}`}
                    content={segment.content}
                    isStreaming={isStreaming}
                  />
                );
              } else {
                // Key 策略：card-${message.id}-${restaurant.id}（更稳，避免 id 意外重复）
                return (
                  <motion.div
                    key={`card-${message.id}-${segment.restaurant.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="my-3"
                  >
                    <RestaurantCard
                      restaurant={segment.restaurant}
                      index={segment.index}
                      variant="chat"
                      isFavorite={favoriteStatus[segment.restaurant.id] || false}
                      onFavoriteClick={handleFavoriteClick}
                    />
                  </motion.div>
                );
              }
            })}
          </div>
        );
      } else {
        // 方案 A 降级模式：intro + 卡片列表（无 reasons）
        logger.log('📦 [1.3.8] 使用策略 A 降级模式（intro + 卡片列表）:', {
          messageId: message.id,
          restaurantsCount: restaurants.length,
          hasContent: !!originalContent,
        });

        return (
          <div className="space-y-3">
            {/* intro 文本（如果有） */}
            {originalContent && originalContent.trim() && (
              <MarkdownRenderer
                key={`text-${message.id}-intro`}
                content={originalContent.trim()}
                isStreaming={message.status === 'streaming'}
              />
            )}

            {/* 卡片列表 */}
            {restaurants.map((restaurant, idx) => (
              <motion.div
                key={`card-${message.id}-${restaurant.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="my-3"
              >
                <RestaurantCard
                  restaurant={restaurant}
                  index={idx + 1}
                  variant="chat"
                  isFavorite={favoriteStatus[restaurant.id] || false}
                  onFavoriteClick={handleFavoriteClick}
                />
              </motion.div>
            ))}
          </div>
        );
      }
    }

    // ========================================
    // Legacy Fallback：旧逻辑（历史消息兼容）
    // 1.3.8: 以下代码注释保留，作为 fallback 和历史消息兼容
    // ========================================

    // 尝试解析 JSON 结构化数据（旧格式）
    let structuredData: StructuredMessage | null = null;
    try {
      const trimmed = originalContent.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && Array.isArray(parsed.restaurants)) {
          structuredData = parsed as StructuredMessage;
        }
      }
    } catch (error) {
      // Not JSON format
    }

    // 如果是旧的 JSON 结构化格式，使用旧渲染逻辑
    if (structuredData && structuredData.restaurants.length > 0) {
      logger.log('📜 [1.3.8] 使用 Legacy JSON 结构化渲染（历史兼容）:', {
        messageId: message.id,
        structuredRestaurantsCount: structuredData.restaurants.length,
      });

      const messageRestaurants = message.restaurants || [];

      return (
        <div className="space-y-4">
          {/* 介绍文本 */}
          {structuredData.introText && (
            <MarkdownRenderer
              key={`text-${message.id}-intro`}
              content={structuredData.introText}
              isStreaming={message.status === 'streaming'}
            />
          )}

          {/* 结构化餐厅列表（旧逻辑：名称匹配） */}
          {structuredData.restaurants.map((item, idx) => {
            // 旧的名称匹配逻辑（fallback）
            const normalizeString = (str: string): string => {
              return str
                .toLowerCase()
                .trim()
                .replace(/\s+/g, ' ')
                .replace(/[，。、]/g, '')
                .replace(/[,.\-]/g, '')
                .replace(/[（）()]/g, '');
            };

            const matchedRestaurant = messageRestaurants.find(r => {
              const searchName = normalizeString(item.restaurantName);
              if (normalizeString(r.name) === searchName) return true;
              if (r.nameZh && normalizeString(r.nameZh) === searchName) return true;
              if (r.nameJa && normalizeString(r.nameJa) === searchName) return true;
              const normalizedRName = normalizeString(r.name);
              if (searchName.includes(normalizedRName) || normalizedRName.includes(searchName)) return true;
              if (item.restaurant && r.id === item.restaurant.id) return true;
              return false;
            });

            // 如果名称匹配失败，使用索引 fallback
            const fallbackRestaurant = matchedRestaurant || messageRestaurants[idx] || messageRestaurants[0];

            return (
              <div key={`structured-restaurant-${message.id}-${idx}`} className="space-y-2">
                <div className="font-semibold text-lg">
                  <span className="text-gray-700">{item.index}.</span>{' '}
                  <span className="font-bold text-gray-900">{item.restaurantName}</span>
                </div>

                {item.description && (
                  <MarkdownRenderer
                    content={item.description}
                    isStreaming={false}
                  />
                )}

                {fallbackRestaurant && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="my-3"
                  >
                    <RestaurantCard
                      restaurant={fallbackRestaurant}
                      index={item.index}
                      variant="chat"
                      isFavorite={favoriteStatus[fallbackRestaurant.id] || false}
                      onFavoriteClick={handleFavoriteClick}
                    />
                  </motion.div>
                )}
              </div>
            );
          })}

          {structuredData.outroText && (
            <MarkdownRenderer
              key={`text-${message.id}-outro`}
              content={structuredData.outroText}
              isStreaming={false}
            />
          )}
        </div>
      );
    }

    // 纯文本渲染（无餐厅数据）
    return (
      <MarkdownRenderer
        key={`text-${message.id}-content`}
        content={originalContent}
        isStreaming={message.status === 'streaming'}
      />
    );
  };

  return (
    <div className="relative h-full min-h-0">
      {/* Messages Container — 底部留出 padding 避免被悬浮输入框遮挡 */}
      <div
        ref={chatContainerRef}
        // 2.0.0: 消息区域背景色 + 相对定位（为世界地图背景层提供定位上下文）
        className="h-full overflow-y-auto px-2 xs:px-3 sm:px-4 md:px-6 pt-4 xs:pt-5 sm:pt-6 pb-24 xs:pb-28 space-y-3 xs:space-y-4 scroll-smooth relative"
        style={{ backgroundColor: 'var(--chat-area-bg)' }}
      >
        {/* 2.0.2: Travel background only shows on welcome screen (inside WelcomeScreen component).
            When messages exist, no background — keeps chat content clean and unobstructed. */}
        {/* 2.0.0: No messages => show WelcomeScreen (contains travel bg + animated planes) */}
        {messages.length === 0 ? (
          <WelcomeScreen onPresetClick={handlePresetClick} />
        ) : (
          <AnimatePresence>
            {messages.map((message: ChatMessage) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`flex gap-2 xs:gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
              >
                {/* 2.0.0: AI 头像改为 gray-800 纯色背景 + "AI" 文字，双主题统一 */}
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0 w-7 h-7 xs:w-8 xs:h-8 bg-gray-800 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">AI</span>
                  </div>
                )}

                {/* 2.0.0: 消息气泡样式 — 用户气泡橙色加深(600)并带 rounded-tr-none 缺口；
                    AI 气泡改为白色/dark 灰色带边框，rounded-tl-none 缺口，支持 dark mode */}
                <div
                  className={`max-w-[85%] xs:max-w-[80%] md:max-w-[70%] px-4 xs:px-5 py-3 xs:py-4 ${message.role === 'user'
                    ? 'bg-orange-600 text-white shadow-lg rounded-2xl rounded-tr-none'
                    : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-white shadow-md hover:shadow-lg transition-shadow duration-200 rounded-2xl rounded-tl-none'
                    }`}
                >
                  {message.role === 'user' ? (
                    <div className="space-y-2">
                      {message.imageUrl && (
                        <div className="rounded-lg overflow-hidden">
                          <img
                            src={message.imageUrl}
                            alt="Uploaded food"
                            className="w-full max-w-xs h-auto object-cover rounded-lg"
                          />
                        </div>
                      )}
                      {message.content && (
                        <p className="whitespace-pre-wrap break-words text-sm xs:text-base">{message.content}</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 xs:space-y-3">
                      {message.status === 'error' ? (
                        // 2.0.0: 错误文本 dark mode 适配
                        <p className="text-red-600 dark:text-red-400 text-sm xs:text-base">
                          {t('chatError', '出错了')}: {message.error}
                        </p>
                      ) : message.status === 'streaming' && !message.content ? (
                        /* 1.6.0: 方案 B — streaming 消息内容为空时，在气泡内展示 Agent 处理进度
                           progress 事件发生在 assistant 占位消息已创建但尚未收到 chunk 的阶段，
                           直接在消息气泡内显示进度文字，当第一个 chunk 到达后自动切换到正常渲染 */
                        <div className="flex items-center gap-2 py-0.5">
                          <Loader2 className="w-4 h-4 xs:w-5 xs:h-5 text-orange-500 animate-spin flex-shrink-0" />
                          {progressMessage ? (
                            <motion.span
                              key={progressMessage}
                              initial={{ opacity: 0, x: -5 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.2 }}
                              className="text-sm text-gray-500"
                            >
                              {progressMessage}
                            </motion.span>
                          ) : (
                            <span className="text-sm text-gray-400">
                              {t('thinking', '思考中...')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <>
                          {renderMessageContent(message)}
                          {/* Feedback buttons and Share button - only show for completed AI messages */}
                          {message.status === 'completed' && message.content && (
                            <div className="flex items-center justify-end gap-1.5 pt-1">
                              {/* Feedback buttons: good (left) -> bad (right) */}
                              <FeedbackButtons messageId={message.id} />
                              {/* Share button */}
                              <button
                                onClick={() => handleShareMessage(message)}
                                disabled={isCreatingShare && sharingMessageId === message.id}
                                // 2.0.0: 分享按钮 dark mode 适配
                                className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={t('shareButton', '分享')}
                              >
                                {isCreatingShare && sharingMessageId === message.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    <span>{t('sharing', '分享中...')}</span>
                                  </>
                                ) : (
                                  <>
                                    <Share2 className="w-3 h-3" />
                                    <span>{t('shareButton', '分享')}</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {message.role === 'user' && (
                  // 2.0.0: 用户头像背景改为 orange-600，双主题统一（原 1.2.19 用户头像显示逻辑保持不变）
                  <div className="flex-shrink-0 w-7 h-7 xs:w-8 xs:h-8 bg-orange-600 rounded-full flex items-center justify-center overflow-hidden">
                    {userAvatarUrl ? (
                      <img
                        src={userAvatarUrl}
                        alt="User avatar"
                        className="w-full h-full object-cover"
                        onError={() => {
                          // 1.2.19: 头像加载失败时回退到默认图标
                          if (import.meta.env.DEV) {
                            logger.warn('Failed to load user avatar, falling back to default icon');
                          }
                          setUserAvatarUrl(null);
                        }}
                      />
                    ) : (
                      // 2.0.0: 默认 User 图标改为白色，与 orange-600 背景搭配
                      <User className="w-4 h-4 xs:w-5 xs:h-5 text-white" />
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {/* Typing indicator — 仅在没有 streaming 消息时显示（极短窗口，作为 fallback）
            1.6.0: progress 进度文字已迁移到 streaming 消息气泡内部展示（方案 B） */}
        {isTyping && messages.length > 0 && !messages.some(msg => msg.role === 'assistant' && msg.status === 'streaming') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-2 xs:gap-3 justify-start"
          >
            {/* 2.0.0: Typing indicator AI 头像，与消息中的 AI 头像保持一致 */}
            <div className="flex-shrink-0 w-7 h-7 xs:w-8 xs:h-8 bg-gray-800 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">AI</span>
            </div>
            {/* 2.0.0: Typing indicator 气泡样式，添加 border + dark mode */}
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl xs:rounded-2xl px-3 xs:px-4 py-2 xs:py-3">
              <Loader2 className="w-4 h-4 xs:w-5 xs:h-5 text-orange-500 animate-spin" />
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area — 悬浮在对话区域底部
          2.0.0: 毛玻璃背景 + 渐变遮罩，与消息区域无明显分割线 */}
      <div className="absolute bottom-0 left-0 right-0 px-3 xs:px-4 sm:px-6 pb-3 xs:pb-4 pt-6
                      bg-gradient-to-t from-white via-white/95 to-transparent
                      dark:from-gray-950 dark:via-gray-950/95 dark:to-transparent
                      pointer-events-none z-10">
        {/* 内层恢复点击事件 */}
        <div className="pointer-events-auto">
        {/* Image Preview */}
        {imagePreview && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`${isSidebarCollapsed ? '' : 'max-w-5xl mx-auto'} mb-2 xs:mb-3`}
          >
            <div className="relative rounded-2xl overflow-hidden border-2 border-orange-300 dark:border-orange-600">
              <img
                src={imagePreview}
                alt="Uploaded food"
                className="w-full h-32 xs:h-36 sm:h-40 object-cover"
              />
              <button
                onClick={handleClearImage}
                className="absolute top-1.5 xs:top-2 right-1.5 xs:right-2 p-1.5 xs:p-2 rounded-full bg-red-500 text-white
                         hover:bg-red-600 active:scale-95 transition-all duration-200"
              >
                <X className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
          disabled={isStreaming}
        />

        {/* 截图风格: 圆角胶囊容器，内含输入框 + 右侧操作按钮 */}
        <div className={`${isSidebarCollapsed ? '' : 'max-w-5xl mx-auto'} relative flex items-center gap-2 
                        border border-orange-300 dark:border-orange-500 
                        rounded-full 
                        bg-white dark:bg-gray-900 
                        px-4 xs:px-5 py-1.5 xs:py-2
                        focus-within:border-orange-500 dark:focus-within:border-orange-400
                        focus-within:ring-2 focus-within:ring-orange-100 dark:focus-within:ring-orange-900/30
                        transition-all duration-200`}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('chatInputPlaceholder', '告诉我你的需求...')}
            disabled={isStreaming}
            className="flex-1 py-1.5 xs:py-2 text-sm xs:text-base 
                     bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500
                     focus:outline-none
                     disabled:cursor-not-allowed"
          />

          {/* 右侧操作按钮组 — 内嵌在输入框中 */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* 图片上传按钮 */}
            <button
              onClick={handleImageButtonClick}
              disabled={isStreaming}
              className="p-1.5 xs:p-2 rounded-full text-gray-400 dark:text-gray-500
                       hover:text-orange-500 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-all duration-200"
              title={t('uploadImage', '上传图片')}
              aria-label={t('uploadImage', '上传菜品图片')}
            >
              <Plus className="w-4 h-4 xs:w-5 xs:h-5" />
            </button>

            {/* 发送按钮 — 有内容时显示橙色，无内容时显示地球图标 */}
            {(input.trim() || uploadedImage) ? (
              <button
                onClick={handleSend}
                disabled={isStreaming}
                className="p-1.5 xs:p-2 rounded-full bg-orange-500 text-white
                         hover:bg-orange-600 active:scale-95
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all duration-200"
                aria-label={isStreaming ? t('loading', '发送中...') : t('send', '发送')}
              >
                {isStreaming ? (
                  <Loader2 className="w-4 h-4 xs:w-5 xs:h-5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 xs:w-5 xs:h-5" />
                )}
              </button>
            ) : (
              <div className="p-1.5 xs:p-2 text-gray-400 dark:text-gray-500">
                <svg className="w-4 h-4 xs:w-5 xs:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* 1.3.0: GPS定位授权弹窗 */}
      <AnimatePresence>
        {showLocationModal && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setShowLocationModal(false);
                setLocationError(null);
                // 1.3.0: 如果用户点击overlay关闭弹窗，清除待搜索的查询
                if (pendingSearchQuery) {
                  setPendingSearchQuery(null);
                }
              }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-black border border-gray-800 rounded-xl shadow-2xl w-full max-w-md pointer-events-auto">
                {/* Content */}
                <div className="p-6">
                  {/* Title */}
                  <h3 className="text-white text-lg font-semibold mb-4">
                    {t('locationPermissionTitle', '需要定位权限')}
                  </h3>

                  {/* Message */}
                  <div className="mb-6 space-y-2">
                    <p className="text-white text-base leading-relaxed">
                      {t('locationPermissionMessage', '为了为您推荐附近的餐厅，我们需要获取您的位置信息。')}
                    </p>
                    {locationError && (
                      <p className="text-red-400 text-sm leading-relaxed">
                        {locationError}
                      </p>
                    )}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    {/* Allow Button */}
                    <button
                      onClick={async () => {
                        if (isRequestingLocation) return; // 防止重复点击
                        setIsRequestingLocation(true);
                        setLocationError(null);

                        try {
                          // 1.3.0: 直接调用 getCurrentPosition 触发浏览器的权限请求
                          // 这会确保浏览器显示定位权限弹窗，并在设置中显示定位权限开关
                          logger.log('📍 开始请求GPS定位权限...');
                          const position = await getCurrentPosition();

                          setUserLocation(position);
                          setLocationError(null);
                          setShowLocationModal(false);
                          setIsRequestingLocation(false);

                          // 1.3.0: 如果有待搜索的查询，现在执行搜索
                          if (pendingSearchQuery) {
                            const { message, image, imagePreview } = pendingSearchQuery;
                            setPendingSearchQuery(null);
                            // 执行搜索
                            await executeSearchWithLocation(message, image, imagePreview, position);
                          }
                        } catch (error) {
                          const errorMessage = error instanceof Error ? error.message : handleLocationError(error as GeolocationPositionError);
                          logger.error('❌ GPS定位失败:', error);

                          // 如果是权限被拒绝，提供更详细的提示
                          if (error instanceof Error && (
                            error.message.includes('权限') ||
                            error.message.includes('denied') ||
                            error.message.includes('PERMISSION_DENIED')
                          )) {
                            setLocationError('定位权限被拒绝。请点击浏览器地址栏左侧的锁图标（或信息图标），在"位置信息"中允许定位权限，然后点击"重置权限"按钮重试。');
                          } else {
                            setLocationError(errorMessage);
                          }
                          setIsRequestingLocation(false);
                        }
                      }}
                      disabled={isRequestingLocation}
                      className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg
                               hover:bg-orange-600 active:scale-95
                               disabled:bg-orange-400 disabled:cursor-not-allowed
                               transition-all duration-200 font-medium relative z-10"
                    >
                      {isRequestingLocation ? t('requestingLocation', '请求中...') : t('allowLocation', '允许')}
                    </button>

                    {/* Deny Button */}
                    <button
                      onClick={() => {
                        setShowLocationModal(false);
                        setLocationError(null);
                        // 1.3.0: 如果用户拒绝，清除待搜索的查询，不执行搜索
                        if (pendingSearchQuery) {
                          setPendingSearchQuery(null);
                        }
                      }}
                      className="flex-1 px-4 py-2.5 bg-gray-700 text-white rounded-lg
                               hover:bg-gray-600 active:scale-95
                               transition-all duration-200 font-medium relative z-10"
                    >
                      {t('denyLocation', '拒绝')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Limit Modal */}
      <AnimatePresence>
        {showLimitModal && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                logger.log('🔄 Closing limit modal (overlay click)');
                setShowLimitModal(false);
                // Reset flag when modal is closed so it can be shown again if needed
                limitModalShownRef.current = false;
              }}
              className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-black border border-gray-800 rounded-xl shadow-2xl w-full max-w-md pointer-events-auto">
                {/* Content */}
                <div className="p-6">
                  {/* Message */}
                  <div className="mb-6 space-y-2">
                    <p className="text-white text-base leading-relaxed">
                      {t('limitModalMessage')}
                    </p>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {t('limitModalSuggestion')}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    {/* Cancel Button */}
                    <button
                      onClick={() => {
                        logger.log('🔄 Closing limit modal (cancel button)');
                        setShowLimitModal(false);
                        // Reset flag when modal is closed so it can be shown again if needed
                        limitModalShownRef.current = false;
                      }}
                      className="flex-1 px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors duration-200"
                    >
                      {t('limitModalCancel')}
                    </button>

                    {/* Register Button */}
                    <button
                      onClick={() => {
                        logger.log('🔄 Closing limit modal and opening auth modal');
                        setShowLimitModal(false);
                        // Reset flag when modal is closed so it can be shown again if needed
                        limitModalShownRef.current = false;
                        setShowAuthModal(true);
                      }}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200"
                    >
                      {t('limitModalRegister')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          // Optionally: refresh conversation or retry save
          onConversationSaved?.();
        }}
        initialMode="signup"
      />

      {/* Share Modal - Fallback for browsers without Web Share API */}
      {shareUrl && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setShareUrl(null);
          }}
          shareUrl={shareUrl}
          title={t('shareModalTitle', '分享链接')}
        />
      )}
    </div>
  );
}

